export {
  buildGoogleUserData,
  buildUetPid,
  buildMetaUserData,
  buildOaiqUser,
  buildSnapUserData,
  buildTiktokUser,
  buildWhopUserData,
  normalizeEmail,
  normalizeMetaPhone,
  normalizeMetaName,
  normalizeOpenAiPhone,
  normalizeOpenAiName,
  normalizeSnapPhone,
  normalizeTiktokPhone,
  sha256Hex,
  isHashingAvailable,
};
export type { MatchIdentity };

/**
 * Advanced matching: identidad del visitante que acompaña al evento de píxel
 * para que la red pueda casarlo con un usuario suyo.
 *
 * Cada red espera cosas distintas y por eso esto vive aquí y no en los
 * adaptadores:
 *
 * - **Meta**: valores EN CLARO (`fbq('init', id, {em, ph, fn, ln, external_id})`);
 *   el propio píxel normaliza y hashea. Mandar un SHA-256 donde espera claro
 *   lo hashearía dos veces y no casaría con nada.
 * - **OpenAI**: SOLO pre-hasheado SHA-256 (`email_sha256`, `phone_number_sha256`,
 *   `first_name_sha256`, `last_name_sha256`, `external_id_sha256`) y con
 *   normalización propia: teléfono de 8-15 dígitos sin `+` ni ceros iniciales;
 *   nombres en minúsculas sin espacios ni puntuación ASCII.
 * - **Whop**: EN CLARO (`email`, `phone`, `first_name`, `last_name`) — su
 *   píxel DESCARTA lo que llega hasheado. Sin external_id: su identidad de
 *   visitante es la cookie `_wuid` que pone el propio píxel.
 * - **Google**: EN CLARO en `gtag('set', 'user_data', {email, phone_number,
 *   address: {first_name, last_name}})` (conversiones mejoradas web); la
 *   etiqueta normaliza y hashea. El teléfono, solo en E.164 (con `+`).
 * - **Microsoft (UET)**: EN CLARO en `uetq.push('set', {pid: {em, ph}})`
 *   (conversiones mejoradas); la UET normaliza el email y hashea los dos en el
 *   navegador. El teléfono lo tiene que llevar ya en E.164 (con `+`).
 * - **TikTok**: SHA-256 previo en `ttq.instance(id).identify({email,
 *   phone_number, external_id})`. El teléfono, en E.164 (con `+`) ANTES de
 *   hashear; `external_id`, el visitor_id hasheado.
 * - **Snapchat**: EN CLARO en `snaptr('init', id, {user_email,
 *   user_phone_number})`; el píxel normaliza y hashea en el navegador
 *   (síncrono, sin `crypto.subtle`). El teléfono, solo dígitos con el prefijo
 *   de país, como el `ph` de la Conversions API.
 *
 * El hash usa `crypto.subtle`, que SOLO existe en contextos seguros: en
 * http://localhost o en un http plano no hay hash y se manda el evento SIN
 * datos de usuario (peor matching, nunca un dato en claro donde la red espera
 * hash, ni una excepción).
 */

interface MatchIdentity {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  // visitor_id del SDK: `external_id` en Meta (en claro) y en TikTok
  // (hasheado), `external_id_sha256` en OpenAI
  visitorId?: string | null;
}

// Puntuación ASCII + espacios: lo que OpenAI manda quitar de los nombres. Los
// acentos (no ASCII) se conservan a propósito: "josé" es "josé" para ellos.
const ASCII_PUNCTUATION = /[\s!-/:-@[-`{-~]/g;

function asText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeEmail(email: unknown): string {
  return asText(email).trim().toLowerCase();
}

/** Meta: solo dígitos (incluido el prefijo de país si viene). */
function normalizeMetaPhone(phone: unknown): string {
  return asText(phone).replace(/[^0-9]/g, "");
}

/** Meta: minúsculas y sin espacios sobrantes; el píxel hace el resto. */
function normalizeMetaName(name: unknown): string {
  return asText(name).trim().toLowerCase();
}

/**
 * OpenAI: 8-15 dígitos, sin `+`, sin ceros iniciales y sin separadores.
 * "+1 (415) 555-2671" -> "14155552671". Fuera de rango devuelve "" (no se
 * manda un teléfono que la red va a descartar igualmente).
 */
function normalizeOpenAiPhone(phone: unknown): string {
  const digits = asText(phone).replace(/[^0-9]/g, "").replace(/^0+/, "");
  if (digits.length < 8 || digits.length > 15) {
    return "";
  }
  return digits;
}

/**
 * TikTok: E.164 con el `+` y sin separadores ("+34 600-111-222" ->
 * "+34600111222"), que es lo que se hashea. Sin `+` no se sabe de qué país es
 * (como en Google y Microsoft) y fuera de 8-15 dígitos no es un E.164: "".
 */
function normalizeTiktokPhone(phone: unknown): string {
  const raw = asText(phone).trim();
  const digits = raw.replace(/[^0-9]/g, "");
  if (raw.charAt(0) !== "+" || digits.length < 8 || digits.length > 15) {
    return "";
  }
  return "+" + digits;
}

/**
 * Snapchat: solo dígitos, con el prefijo de país si viene ("+34 600-111-222"
 * -> "34600111222"): lo mismo que hashea la Conversions API como `ph`, para
 * que las dos copias del evento casen con el mismo usuario.
 */
function normalizeSnapPhone(phone: unknown): string {
  return asText(phone).replace(/[^0-9]/g, "");
}

/** OpenAI: minúsculas, sin espacios ni puntuación ASCII ("O'Connor" -> "oconnor"). */
function normalizeOpenAiName(name: unknown): string {
  return asText(name).toLowerCase().replace(ASCII_PUNCTUATION, "");
}

function isHashingAvailable(): boolean {
  try {
    return (
      typeof crypto !== "undefined" &&
      !!crypto.subtle &&
      typeof crypto.subtle.digest === "function" &&
      typeof TextEncoder !== "undefined"
    );
  } catch (e) {
    return false;
  }
}

async function sha256Hex(value: string): Promise<string> {
  if (value === "" || !isHashingAvailable()) {
    return "";
  }
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const view = new Uint8Array(digest);
  let hex = "";
  for (let i = 0; i < view.length; i++) {
    hex += view[i].toString(16).padStart(2, "0");
  }
  return hex;
}

/** Meta: valores en claro, omitiendo los vacíos. */
function buildMetaUserData(identity: MatchIdentity | null): Record<string, string> {
  const data: Record<string, string> = {};
  if (!identity) {
    return data;
  }
  try {
    const em = normalizeEmail(identity.email);
    const ph = normalizeMetaPhone(identity.phone);
    const fn = normalizeMetaName(identity.firstName);
    const ln = normalizeMetaName(identity.lastName);
    const externalId = asText(identity.visitorId).trim();

    if (em !== "") data.em = em;
    if (ph !== "") data.ph = ph;
    if (fn !== "") data.fn = fn;
    if (ln !== "") data.ln = ln;
    // external_id: el visitor_id del SDK. Formatos numérico legacy y uuidv7
    // conviven; para la red es una cadena opaca, aquí no se parsea nada.
    if (externalId !== "") data.external_id = externalId;
  } catch (e) {
    return {};
  }
  return data;
}

/**
 * Google (conversiones mejoradas web, `gtag('set', 'user_data', …)`): en
 * claro, la etiqueta normaliza y hashea. El teléfono solo si viene en E.164
 * (`+` y prefijo de país): sin prefijo no se puede saber de qué país es y
 * Google lo descartaría. Sin external_id: Google no lo usa para casar.
 */
function buildGoogleUserData(
  identity: MatchIdentity | null,
): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  if (!identity) {
    return data;
  }
  try {
    const email = normalizeEmail(identity.email);
    const rawPhone = asText(identity.phone).trim();
    const digits = rawPhone.replace(/[^0-9]/g, "");
    const firstName = asText(identity.firstName).trim();
    const lastName = asText(identity.lastName).trim();

    if (email !== "") data.email = email;
    if (rawPhone.charAt(0) === "+" && digits.length >= 8) {
      data.phone_number = "+" + digits;
    }
    const address: Record<string, string> = {};
    if (firstName !== "") address.first_name = firstName;
    if (lastName !== "") address.last_name = lastName;
    if (Object.keys(address).length > 0) data.address = address;
  } catch {
    return {};
  }
  return data;
}

/**
 * Microsoft (UET): `pid` de las conversiones mejoradas, en claro. Email en
 * minúsculas sin espacios (la UET quita además alias y puntos antes de
 * hashear); teléfono solo si ya viene en E.164, que es lo que exige Microsoft.
 * Sin email ni teléfono, vacío (no se manda `pid`).
 */
function buildUetPid(identity: MatchIdentity | null): Record<string, string> {
  const pid: Record<string, string> = {};
  if (!identity) {
    return pid;
  }
  try {
    const email = normalizeEmail(identity.email);
    const rawPhone = asText(identity.phone).trim();
    const digits = rawPhone.replace(/[^0-9]/g, "");
    if (email !== "") pid.em = email;
    if (rawPhone.charAt(0) === "+" && digits.length >= 8) {
      pid.ph = "+" + digits;
    }
  } catch {
    return {};
  }
  return pid;
}

/**
 * Whop: valores en claro, omitiendo los vacíos. Normalización mínima y sin
 * pérdida: email trim + minúsculas; teléfono sin separadores conservando el
 * `+` inicial ("+34 600-111-222" → "+34600111222", E.164 si llega con
 * prefijo); nombres solo recortados.
 */
function buildWhopUserData(
  identity: MatchIdentity | null,
): Record<string, string> {
  const data: Record<string, string> = {};
  if (!identity) {
    return data;
  }
  try {
    const email = normalizeEmail(identity.email);
    const rawPhone = asText(identity.phone).trim();
    const digits = rawPhone.replace(/[^0-9]/g, "");
    const phone =
      digits === "" ? "" : (rawPhone.charAt(0) === "+" ? "+" : "") + digits;
    const firstName = asText(identity.firstName).trim();
    const lastName = asText(identity.lastName).trim();

    if (email !== "") data.email = email;
    if (phone !== "") data.phone = phone;
    if (firstName !== "") data.first_name = firstName;
    if (lastName !== "") data.last_name = lastName;
  } catch {
    return {};
  }
  return data;
}

/** OpenAI: todo SHA-256; sin contexto seguro devuelve {} (evento sin usuario). */
async function buildOaiqUser(
  identity: MatchIdentity | null,
): Promise<Record<string, string>> {
  const user: Record<string, string> = {};
  if (!identity || !isHashingAvailable()) {
    return user;
  }
  try {
    const [email, phone, firstName, lastName, externalId] = await Promise.all([
      sha256Hex(normalizeEmail(identity.email)),
      sha256Hex(normalizeOpenAiPhone(identity.phone)),
      sha256Hex(normalizeOpenAiName(identity.firstName)),
      sha256Hex(normalizeOpenAiName(identity.lastName)),
      sha256Hex(asText(identity.visitorId).trim()),
    ]);

    if (email !== "") user.email_sha256 = email;
    if (phone !== "") user.phone_number_sha256 = phone;
    if (firstName !== "") user.first_name_sha256 = firstName;
    if (lastName !== "") user.last_name_sha256 = lastName;
    if (externalId !== "") user.external_id_sha256 = externalId;
  } catch (e) {
    return {};
  }
  return user;
}

/**
 * TikTok (`identify`): todo SHA-256 — email normalizado, teléfono E.164 y el
 * visitor_id como `external_id` —: los tres campos que documenta el
 * `identify` del píxel; los nombres no viajan. Sin contexto seguro devuelve
 * {} (evento sin usuario).
 */
/**
 * Snapchat: `user_email` y `user_phone_number` EN CLARO para `snaptr('init')`,
 * que los hashea él mismo antes de enviarlos. Síncrono, así que el evento del
 * propio optin ya sale con la identidad. Sin external_id: el píxel no tiene
 * campo para él (su identidad de visitante es la cookie `_scid`).
 */
function buildSnapUserData(identity: MatchIdentity | null): Record<string, string> {
  const user: Record<string, string> = {};
  if (!identity) {
    return user;
  }
  const email = normalizeEmail(identity.email);
  const phone = normalizeSnapPhone(identity.phone);
  if (email !== "") user.user_email = email;
  if (phone !== "") user.user_phone_number = phone;
  return user;
}

async function buildTiktokUser(
  identity: MatchIdentity | null,
): Promise<Record<string, string>> {
  const user: Record<string, string> = {};
  if (!identity || !isHashingAvailable()) {
    return user;
  }
  try {
    const [email, phone, externalId] = await Promise.all([
      sha256Hex(normalizeEmail(identity.email)),
      sha256Hex(normalizeTiktokPhone(identity.phone)),
      sha256Hex(asText(identity.visitorId).trim()),
    ]);

    if (email !== "") user.email = email;
    if (phone !== "") user.phone_number = phone;
    if (externalId !== "") user.external_id = externalId;
  } catch {
    return {};
  }
  return user;
}
